/**
 * Process-wide singleton `ResizeObserver`.
 *
 * Every live board mounts a viewport-fit observer (`useViewport`) AND a
 * canvas-size observer (`BoardCanvas`) — historically two `new ResizeObserver`
 * per board on the SAME container element. At catalog scale (N boards on a
 * page) that is 2·N observer instances, each its own native allocation and
 * callback bookkeeping, plus two native observations of one node per board.
 *
 * One shared observer watches every registered element and fans each resize
 * entry out to the callbacks registered for that element. The observer OBJECT
 * is O(1) regardless of board count; observations stay O(distinct elements) but
 * collapse to ONE native `observe` per element even when several hooks watch the
 * same node.
 *
 * Behaviour is otherwise identical to a per-hook observer: `ResizeObserver`
 * delivers an initial entry for each newly-observed target, so callers that
 * relied on the mount-time fire (fit / size seed) still get it.
 */
type ResizeCb = (entry: ResizeObserverEntry) => void;

let observer: ResizeObserver | null = null;
/** element → its live callbacks. WeakMap so a detached node's entry is GC'd
 *  even if a caller leaks its disposer. `let` only so the test reset can swap in
 *  a fresh map. */
let registry = new WeakMap<Element, Set<ResizeCb>>();

function ensureObserver(): ResizeObserver | null {
  if (typeof ResizeObserver === "undefined") return null;
  if (observer) return observer;
  observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const cbs = registry.get(entry.target);
      if (!cbs) continue;
      // Snapshot: a callback may dispose itself (or a sibling) mid-dispatch
      // — e.g. a dive/pop that unmounts the board during a resize burst.
      for (const cb of [...cbs]) cb(entry);
    }
  });
  return observer;
}

/**
 * Observe `el` with the shared observer, invoking `cb` on resize. Returns a
 * disposer that removes THIS callback; when it was the last callback for the
 * element the element is unobserved. Safe to call before `ResizeObserver`
 * exists (SSR / jsdom without the polyfill) — it no-ops and the disposer is a
 * no-op.
 *
 * The FIRST callback for an element triggers `observe(el)`, so it receives the
 * mount-time fire `ResizeObserver` delivers for a newly-observed target. A
 * later callback added to an ALREADY-observed element (e.g. two board hooks
 * watching one container) does NOT get a synthetic re-fire — it starts
 * receiving real resize entries on the next actual size change, which is all
 * the board hooks need (initial size is seeded by their own layout effect / a
 * dedicated fit effect, not this callback's first tick).
 */
export function observeResize(el: Element, cb: ResizeCb): () => void {
  const obs = ensureObserver();
  if (!obs) return () => {};
  let cbs = registry.get(el);
  const isNew = !cbs;
  if (!cbs) {
    cbs = new Set();
    registry.set(el, cbs);
  }
  // Register BEFORE observing so a synchronous mount-fire (real RO fires async,
  // but a test double may fire inside observe()) already sees this callback.
  cbs.add(cb);
  if (isNew) obs.observe(el); // first callback for this element → observe once
  let disposed = false;
  return () => {
    if (disposed) return; // idempotent: React may run cleanup more than once
    disposed = true;
    const set = registry.get(el);
    if (!set) return;
    set.delete(cb);
    if (set.size === 0) {
      registry.delete(el);
      obs.unobserve(el);
    }
  };
}

/** Test-only: drop the singleton + registry so a fresh fake `ResizeObserver`
 *  installed by a test is the one used. Never called by app code. */
export function __resetSharedResizeObserverForTest(): void {
  observer = null;
  registry = new WeakMap<Element, Set<ResizeCb>>();
}
